import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

dayjs.extend(utc);

export type SubscriptionInfo = NonNullable<
  components['schemas']['SubscriptionResponse']['data']
>;

const DISPLAY_DATE_FORMAT = 'MMMM D, YYYY';

const NO_SUBSCRIPTION_FOUND = 404;

const formatDisplayDate = (isoDate: string | null) =>
  isoDate ? dayjs.utc(isoDate).format(DISPLAY_DATE_FORMAT) : null;

/**
 * A free account having no subscription is a normal state, not a failure: the
 * API answers GET /users/me/subscription with a deliberate 404 for those users.
 * Reporting it as an absence rather than throwing keeps the expected case out
 * of the console and out of Datadog RUM, so a genuine subscription-fetch
 * failure stays visible instead of being buried under one error per free user
 * who opens the account page (FFH-1884).
 */
export type SubscriptionResult =
  | { hasSubscription: true; subscription: SubscriptionInfo }
  | { hasSubscription: false };

export const getUserInfo = async ({
  accessToken
}: {
  accessToken: string;
}): Promise<SubscriptionResult> => {
  const { data, error, response } = await ffhClient().GET(
    '/users/me/subscription',
    {
      headers: authHeader(accessToken)
    }
  );

  if (response.status === NO_SUBSCRIPTION_FOUND) {
    return { hasSubscription: false };
  }

  if (error || !data?.data) {
    throw new Error('Failed to load subscription details');
  }

  const subscription = data.data;

  return {
    hasSubscription: true,
    subscription: {
      ...subscription,
      invoice: subscription.invoice
        ? {
            ...subscription.invoice,
            nextPaymentDate: formatDisplayDate(
              subscription.invoice.nextPaymentDate
            ),
            subscriptionExpiresOn: formatDisplayDate(
              subscription.invoice.subscriptionExpiresOn
            )
          }
        : null
    }
  };
};

type BillingSessionResult =
  | { clientSecret: string; sessionId: string }
  | { error: true };

export const createBillingSession = async ({
  accessToken
}: {
  accessToken: string;
}): Promise<BillingSessionResult> => {
  const { data, error } = await ffhClient().POST(
    '/users/me/subscription/billing',
    {
      headers: authHeader(accessToken)
    }
  );

  if (error || !data?.data) {
    return { error: true };
  }

  return data.data;
};

export const confirmCardUpdate = async ({
  accessToken,
  sessionId
}: {
  accessToken: string;
  sessionId: string;
}): Promise<{ isSuccess: boolean }> => {
  const { error } = await ffhClient().POST(
    '/users/me/subscription/billing/confirm',
    {
      headers: authHeader(accessToken),
      body: { sessionId }
    }
  );

  return { isSuccess: !error };
};
